import { devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Badge, NavDropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { auth } from '../firebase';
import QANavbar from './QANavbar';
import {
  canAccessAssignmentWorkspace,
  canManageSchedules,
  canUseRestrictedMessaging,
  canBroadcastToLearners,
  canViewResources,
  isAssessorRole,
  isExternalFacilitatorRole
} from '../services/roleManagementService';

const FacilitatorNavbar = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [isAdminViewing, setIsAdminViewing] = useState(false);

  useEffect(() => {
    // Check if this is an admin viewing as facilitator
    const viewMode = localStorage.getItem('adminViewMode');
    const isAdmin = userProfile?.role === 'admin';
    setIsAdminViewing(isAdmin && viewMode === 'facilitator');
  }, [userProfile]);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('adminViewMode');
      navigate('/login');
    } catch (error) {
      devError('Failed to log out', error);
    }
  };

  const handleSwitchToAdmin = () => {
    localStorage.removeItem('adminViewMode');
    navigate('/admin');
  };

  if (userProfile?.role === 'qa') {
    return <QANavbar />;
  }

  const role = userProfile?.role;
  const showDashboard = !isAssessorRole(role);
  const showPrograms = !isAssessorRole(role);
  const showResources = canViewResources(role);
  const showComposer = canUseRestrictedMessaging(role);
  const showWhatsApp = canBroadcastToLearners(role);
  const showAnalytics = canManageSchedules(role) || isExternalFacilitatorRole(role);
  const showGradebook = canAccessAssignmentWorkspace(role);

  return (
    <Navbar variant="dark" expand="lg" sticky="top" className="navbar-gradient">
      <Container>
        <Navbar.Brand
          onClick={() => {
            // Avoid sending assessors/external users into facilitator-only routes.
            if (isAssessorRole(role)) {
              navigate('/assessor');
              return;
            }
            navigate('/facilitator');
          }}
          style={{ cursor: 'pointer' }}
          className="d-flex align-items-center"
        >
          <div className="d-flex align-items-center gap-2">
            <img
              src="/Access70Logo.png"
              alt="Access70"
              height="40"
              style={{ maxHeight: '40px', width: 'auto' }}
            />
            <img
              src="/NPI_Logo.png"
              alt="NPI Governance Solutions"
              height="50"
              style={{ maxHeight: '50px', width: 'auto' }}
            />
          </div>
          {isAdminViewing && (
            <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>
              Facilitator View
            </Badge>
          )}
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto">
            {showDashboard && <Nav.Link onClick={() => navigate('/facilitator')}>Dashboard</Nav.Link>}
            {showPrograms && <Nav.Link onClick={() => navigate('/facilitator/programs')}>My Programs</Nav.Link>}
            {showResources && <Nav.Link onClick={() => navigate('/resources')}>Resources</Nav.Link>}
            {showComposer && (
              <NavDropdown title="Composer" id="facilitator-composer-dropdown">
                <NavDropdown.Item onClick={() => navigate('/email-composer')}>
                  Email Composer
                </NavDropdown.Item>
                {showWhatsApp && (
                  <NavDropdown.Item onClick={() => navigate('/whatsapp-composer')}>
                    WhatsApp Composer
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            )}
            {showGradebook && <Nav.Link onClick={() => navigate('/facilitator/gradebook')}>Gradebook</Nav.Link>}
            {showAnalytics && (
              <NavDropdown title="Analytics" id="facilitator-analytics-dropdown">
                {canManageSchedules(role) && (
                  <NavDropdown.Item onClick={() => navigate('/facilitator/attendance')}>
                    Attendance Analytics
                  </NavDropdown.Item>
                )}
                {!isAssessorRole(role) && (
                  <NavDropdown.Item onClick={() => navigate('/facilitator/sales-dashboard')}>
                    Sales Dashboard
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            )}
            <NavDropdown
              title={
                <span>
                  {userProfile?.displayName || currentUser?.email}
                  {isAdminViewing && (
                    <Badge bg="danger" className="ms-2" style={{ fontSize: '0.6rem' }}>Admin</Badge>
                  )}
                </span>
              }
              id="user-dropdown"
              align="end"
            >
              <NavDropdown.Item onClick={() => navigate('/facilitator/profile')}>
                My Profile
              </NavDropdown.Item>
              {isAdminViewing && (
                <>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleSwitchToAdmin} className="text-primary">
                    Switch to Admin View
                  </NavDropdown.Item>
                </>
              )}
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout} className="text-danger">
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default FacilitatorNavbar;
